import { FC, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  DeleteOutlined,
  DownloadOutlined,
  PaperClipOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { usersAPI } from '@/api'
import { selectProfile } from '@store/profile.reducer'
import { useAddAlert } from '@/hooks/useAddAlert'
import { getCorrectFilePath } from '@/helpers/new/getCorrectFilePath'
import cn from 'classnames'
import {
  parseCrmFileFieldValue,
  serializeCrmFileFieldValue,
} from '@pages/Admin/CRM/utils/crmLeadFieldValues'
import classes from './index.module.css'

const EMPTY_FILE_LABEL = 'Файл не загружен'

type LeadFileFieldProps = {
  value?: string
  /** С бэка (`file_name`) — без лишнего getUserFile. */
  fileName?: string | null
  /** С бэка (`file_url`) — абсолютная ссылка на скачивание. */
  fileUrl?: string | null
  onChange: (next: string) => void
  disabled?: boolean
  error?: string
  placeholder?: string
}

const readUserFilePayload = (
  payload: unknown,
): Record<string, unknown> | null => {
  if (!payload || typeof payload !== 'object') return null
  const root = payload as Record<string, unknown>
  const data =
    root.data && typeof root.data === 'object'
      ? (root.data as Record<string, unknown>)
      : root
  const nested =
    data.data && typeof data.data === 'object'
      ? (data.data as Record<string, unknown>)
      : data
  return nested
}

const readFileNameFromPayload = (payload: unknown): string | null => {
  const nested = readUserFilePayload(payload)
  if (!nested) return null
  const name = nested.name ?? nested.file_name ?? nested.original_name
  return typeof name === 'string' && name.trim() ? name.trim() : null
}

const readFilePathFromPayload = (payload: unknown): string | null => {
  const nested = readUserFilePayload(payload)
  if (!nested) return null
  const path = nested.path ?? nested.file_path ?? nested.url
  return typeof path === 'string' && path.trim() ? path.trim() : null
}

const readFileIdFromPayload = (payload: unknown): unknown => {
  const nested = readUserFilePayload(payload)
  if (!nested) return null
  return nested.id ?? nested.user_file_id ?? null
}

const resolveDownloadHref = (pathOrUrl: string) => {
  const trimmed = pathOrUrl.trim()
  if (!trimmed) return null
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  return getCorrectFilePath(trimmed) || null
}

const LeadFileField: FC<LeadFileFieldProps> = ({
  value,
  fileName: fileNameFromApi,
  fileUrl: fileUrlFromApi,
  onChange,
  disabled,
  error,
  placeholder,
}) => {
  const addAlert = useAddAlert()
  const profile = useSelector(selectProfile)
  const inputRef = useRef<HTMLInputElement>(null)
  const fileId = parseCrmFileFieldValue(value)
  const [fileName, setFileName] = useState<string | null>(null)
  const [filePath, setFilePath] = useState<string | null>(null)
  const [loadingMeta, setLoadingMeta] = useState(false)
  const [uploading, setUploading] = useState(false)
  /**
   * После локальной загрузки у лида ещё старые file_name/file_url —
   * пока value = новый id, не даём им перезатереть свежее имя.
   */
  const localFileMetaRef = useRef<{
    fileId: number
    name: string
    path: string | null
  } | null>(null)

  useEffect(() => {
    let cancelled = false

    if (!fileId) {
      localFileMetaRef.current = null
      setFileName(null)
      setFilePath(null)
      setLoadingMeta(false)
      return
    }

    const localMeta =
      localFileMetaRef.current?.fileId === fileId
        ? localFileMetaRef.current
        : null
    if (localMeta) {
      setFileName(localMeta.name)
      setFilePath(localMeta.path)
      setLoadingMeta(false)
      return
    }

    const apiName = fileNameFromApi?.trim() || null
    const apiUrl = fileUrlFromApi?.trim() || null
    if (apiName || apiUrl) {
      setFileName(apiName ?? `Файл #${fileId}`)
      setFilePath(apiUrl)
      setLoadingMeta(false)
      return
    }

    setLoadingMeta(true)
    usersAPI
      .getUserFile(fileId)
      .then((res: unknown) => {
        if (cancelled) return
        setFileName(readFileNameFromPayload(res) ?? `Файл #${fileId}`)
        setFilePath(readFilePathFromPayload(res))
      })
      .catch(() => {
        if (cancelled) return
        setFileName(`Файл #${fileId}`)
        setFilePath(null)
      })
      .finally(() => {
        if (!cancelled) setLoadingMeta(false)
      })

    return () => {
      cancelled = true
    }
  }, [fileId, fileNameFromApi, fileUrlFromApi])

  const handlePickFile = () => {
    if (disabled || uploading) return
    inputRef.current?.click()
  }

  const handleFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    const userId = profile?.id
    if (!userId) {
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось определить пользователя для загрузки файла',
        type: 'error',
      })
      return
    }

    setUploading(true)
    try {
      const body = new FormData()
      body.append('file', file)
      body.append('UserFilesForm[user_id]', String(userId))
      body.append('UserFilesForm[name]', file.name)

      const res = await usersAPI.uploadPlatformMedia(body)
      const uploadedId = parseCrmFileFieldValue(
        readFileIdFromPayload(res) ?? undefined,
      )
      if (!uploadedId) {
        throw new Error('empty id')
      }

      const uploadedPath = readFilePathFromPayload(res)
      localFileMetaRef.current = {
        fileId: uploadedId,
        name: file.name,
        path: uploadedPath,
      }
      setFileName(file.name)
      setFilePath(uploadedPath)
      onChange(serializeCrmFileFieldValue(uploadedId))
    } catch {
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось загрузить файл',
        type: 'error',
      })
    } finally {
      setUploading(false)
    }
  }

  const handleClear = () => {
    if (disabled || uploading) return
    localFileMetaRef.current = null
    setFileName(null)
    setFilePath(null)
    onChange('')
  }

  const handleDownload = () => {
    if (!filePath) {
      addAlert({
        title: 'Ошибка',
        text: 'Ссылка на файл пока недоступна',
        type: 'error',
      })
      return
    }

    const href = resolveDownloadHref(filePath)
    if (!href) {
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось открыть файл',
        type: 'error',
      })
      return
    }

    window.open(href, '_blank', 'noopener,noreferrer')
  }

  const canUpload = !disabled && !uploading
  const displayName = fileId
    ? loadingMeta && !fileName
      ? 'Загрузка…'
      : fileName ?? `Файл #${fileId}`
    : uploading
      ? 'Загрузка…'
      : EMPTY_FILE_LABEL

  return (
    <div className={classes.fileField}>
      <input
        ref={inputRef}
        type="file"
        className={classes.fileFieldHiddenInput}
        disabled={disabled || uploading}
        onChange={(event) => void handleFileSelected(event)}
      />

      <div
        className={cn(
          classes.fileFieldValueRow,
          !fileId && classes.fileFieldValueRow_empty,
          canUpload && !fileId && classes.fileFieldValueRow_clickable,
        )}
        role={!fileId && canUpload ? 'button' : undefined}
        tabIndex={!fileId && canUpload ? 0 : undefined}
        aria-label={!fileId ? placeholder || 'Загрузить файл' : undefined}
        onClick={!fileId ? handlePickFile : undefined}
        onKeyDown={
          !fileId && canUpload
            ? (event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return
                event.preventDefault()
                handlePickFile()
              }
            : undefined
        }
      >
        <PaperClipOutlined
          className={classes.fileFieldIcon}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
        <span
          className={cn(
            classes.fileFieldName,
            !fileId && classes.fileFieldName_empty,
          )}
          title={fileId ? fileName ?? undefined : EMPTY_FILE_LABEL}
        >
          {displayName}
        </span>
        {fileId ? (
          <>
            <button
              type="button"
              className={classes.fileFieldActionButton}
              aria-label="Скачать файл"
              disabled={uploading || loadingMeta || !filePath}
              onClick={handleDownload}
            >
              <DownloadOutlined
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </button>
            {!disabled ? (
              <button
                type="button"
                className={`${classes.fileFieldActionButton} ${classes.fileFieldClearButton}`}
                aria-label="Очистить файл"
                disabled={uploading}
                onClick={handleClear}
              >
                <DeleteOutlined
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              </button>
            ) : null}
          </>
        ) : canUpload ? (
          <span
            className={classes.fileFieldActionButton}
            aria-hidden
          >
            <UploadOutlined
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          </span>
        ) : null}
      </div>

      {error ? <p className={classes.fileFieldError}>{error}</p> : null}
    </div>
  )
}

export default LeadFileField
